import { Injectable, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { exportFile } from 'common-utils/dist/csv-export';
import { HandoverLog } from '../models/handover-log.model';
import { HighAlert } from '../models/high-alert.model';
import { ShiftLog } from '../models/shift-log.model';
import { HighAlertColumn } from '../models/high-alert-column.model';


export type ExportColumnConfig<T> = { header: string, accessor: (log: T) => any };
export type ExportColumns<T> = ReadonlyArray<ExportColumnConfig<T>>;

@Injectable({ providedIn: 'root' })
export class ExportCSVService {
  constructor(private translate: TranslateService) { }

  public exportCSV<T extends HandoverLog | ShiftLog | HighAlert>(handoverLogsSignal: Signal<readonly T[]>, columns: ExportColumns<T>): void {
    const dataForCSV = handoverLogsSignal();
    exportFile(this.buildDataForCSV<T>(dataForCSV, columns), this.buildUniqueFileName());
  }

  private buildDataForCSV<T extends HandoverLog | ShiftLog | HighAlert>(dataForCSV: readonly T[], columns: ExportColumns<T>): string {
    const activeColumns = columns.filter((col) => col.header && col.header.trim() !== '');
    const headers = activeColumns.map((col) => col.header.toLocaleUpperCase());

    // Convert array of objects to CSV
    const csvArray: string[] = [
      headers.join(','), // Header row
      ...dataForCSV.map((obj) => activeColumns.map((col) => {
        return this.escapeCSVValue(col.accessor(obj));
      }).join(',')), // Data rows
    ];

    return csvArray.join('\n');
  }

  /**
   * Escapes a value for CSV format. (Standar CSV escaping)
   * Every value will be enclosed in double quotes.
   * @param value The value to escape (string, boolean or Date).
   * @returns The escaped value.
   */
  private escapeCSVValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Escape any existing double quotes immediately
    const escapedQuotes = String(value).replace(/"/g, '""');

    // Wrap every single value in double quotes unconditionally
    return `"${escapedQuotes}"`;
  }

  private buildUniqueFileName(): string {
    const timestamp: number = Date.now();
    const randomId: string = Math.random().toString(36).substring(7);
    const fileName = `csv_export_logs_${timestamp}_${randomId}`;
    return fileName;
  }
}
